import React, { Fragment } from "react";
import { Box, Typography, Divider } from "@mui/material";

const ROW_COLORS = (isDark) => [
  isDark ? "#8b5cf6" : "#7c3aed",
  isDark ? "#3b82f6" : "#2563eb",
  isDark ? "#4ade80" : "#16a34a",
  isDark ? "#fbbf24" : "#d97706"
];

export default function ScenarioTable({
  title,
  items,
  theme,
  showAnalyze,
  iconRenderMap,
  twoColumn,
  footerNote,
  highlightDominant
}) {
  if (!items || items.length === 0) return null;

  const isDark = theme.palette.mode === "dark";
  const colors = ROW_COLORS(isDark);
  const totalGames = items.reduce((sum, item) => sum + (item.games || 0), 0);
  const maxGames = highlightDominant
    ? Math.max(...items.map((i) => i.games || 0))
    : 0;

  const renderItem = (item, idx, compact) => {
    const color = colors[idx % colors.length];
    const pct = totalGames > 0 ? (item.games / totalGames) * 100 : 0;
    const icon = iconRenderMap?.[item.label];
    const isDominant = highlightDominant && item.games === maxGames;

    return (
      <Box
        key={idx}
        sx={{
          display: "flex",
          alignItems: "stretch",
          py: compact ? 1.25 : 1.75,
          borderRadius: 1,
          backgroundColor: isDominant ? `${color}10` : "transparent",
          mx: isDominant ? -0.5 : 0,
          px: isDominant ? 0.5 : 0
        }}
      >
        <Box
          sx={{
            width: 3,
            borderRadius: 2,
            backgroundColor: color,
            flexShrink: 0,
            mr: icon ? 1.5 : 2,
            my: 0.25
          }}
        />

        {icon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: 1.5,
              flexShrink: 0
            }}
          >
            {icon(color)}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mb: 0.75
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 700,
                fontSize: compact ? "0.8rem" : "0.9rem"
              }}
            >
              {item.label}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 0.5,
                flexShrink: 0
              }}
            >
              <Typography
                sx={{
                  color,
                  fontWeight: 700,
                  fontSize: compact ? "0.875rem" : "1rem",
                  lineHeight: 1.2,
                  fontVariantNumeric: "tabular-nums"
                }}
              >
                {item.games.toLocaleString()}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  fontSize: "0.7rem",
                  lineHeight: 1.2
                }}
              >
                Games
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                flex: 1,
                height: compact ? 6 : 8,
                borderRadius: 4,
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.08)",
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${pct}%`,
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${color}55, ${color})`
                }}
              />
            </Box>
            <Typography
              sx={{
                color,
                fontWeight: 700,
                fontSize: "0.8rem",
                minWidth: 38,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0
              }}
            >
              {pct > 0 ? `${pct.toFixed(1)}%` : "–"}
            </Typography>
            {/* TODO: Add analyze button after demo */}
            {/* {showAnalyze && (
              <Button
                size="small"
                variant="contained"
                sx={{
                  minWidth: 70,
                  height: 28,
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.75rem",
                  backgroundColor: isDark ? "#3b82f6" : "#2563eb",
                  "&:hover": {
                    backgroundColor: isDark ? "#2563eb" : "#1d4ed8"
                  },
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: 1,
                  flexShrink: 0
                }}
              >
                Analyze
              </Button>
            )} */}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        borderRadius: 1,
        p: 2.5,
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.3)"
          : "0 2px 8px rgba(0,0,0,0.1)",
        height: "100%"
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 2.5,
          color: theme.palette.text.secondary,
          textAlign: "center"
        }}
      >
        {title}
      </Typography>

      {twoColumn ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 1,
            rowGap: 0
          }}
        >
          {items.map((item, idx) => {
            const isLastRow =
              idx >= items.length - (items.length % 2 === 0 ? 2 : 1);
            return (
              <Box
                key={idx}
                sx={{
                  borderBottom: isLastRow
                    ? "none"
                    : `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                  borderRight:
                    idx % 2 === 0
                      ? `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`
                      : "none",
                  pr: idx % 2 === 0 ? 1 : 0,
                  pl: idx % 2 !== 0 ? 1 : 0
                }}
              >
                {renderItem(item, idx, true)}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {items.map((item, idx) => (
            <Fragment key={idx}>
              {renderItem(item, idx, false)}
              {idx !== items.length - 1 && (
                <Divider
                  sx={{
                    borderColor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)"
                  }}
                />
              )}
            </Fragment>
          ))}
        </Box>
      )}

      {footerNote && (
        <Typography
          sx={{
            mt: 1.5,
            textAlign: "right",
            color: "rgba(255,255,255,0.45)",
            fontSize: "0.7rem",
            fontVariantNumeric: "tabular-nums"
          }}
        >
          {footerNote}
        </Typography>
      )}
    </Box>
  );
}
